import React, { useEffect, useState } from 'react';
import { Link2, Link2Off, RefreshCw, CheckCircle2, AlertCircle, ExternalLink, KeyRound, Loader2, Users, Building2, Download, Upload, Webhook } from 'lucide-react';
import API from '../../api';
import { useConfirm } from '../../components/ConfirmDialog';

const alertMsg = {
  success: (m) => alert(m),
  error: (m) => alert('Errore: ' + m),
};

export default function FicSdiTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenInput, setTokenInput] = useState('');
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/api/sdi/status');
      setStatus(data);
    } catch (e) {
      alertMsg.error('Impossibile recuperare lo stato SDI');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleSaveToken = async () => {
    const trimmed = tokenInput.trim();
    if (!trimmed) { alertMsg.error('Incolla il token prima di salvare.'); return; }
    if (!trimmed.startsWith('a/')) {
      const ok = await confirm({
        title: 'Token con formato inatteso',
        message: 'Il token FattureInCloud di solito inizia con "a/". Sei sicuro di volerlo salvare comunque?',
        confirmLabel: 'Salva comunque',
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const { data } = await API.post('/api/sdi/manual-token', { token: trimmed });
      alertMsg.success(`Connesso! Azienda: ${data.company_name || data.company_id}`);
      setTokenInput('');
      await fetchStatus();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      alertMsg.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    const ok = await confirm({
      title: 'Disconnettere FattureInCloud?',
      message: 'Il token verrà cancellato dal gestionale. Per revocarlo anche su FIC, vai su Impostazioni → Applicazioni Collegate sul loro sito.',
      confirmLabel: 'Disconnetti',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await API.post('/api/sdi/disconnect');
      alertMsg.success('FattureInCloud disconnesso');
      fetchStatus();
    } catch (e) {
      alertMsg.error('Errore disconnessione');
    }
  };

  if (loading) return <div className="text-slate-500 text-sm">Caricamento stato SDI...</div>;

  return (
    <div className="space-y-6" data-testid="fic-sdi-tab">
      <div className={`rounded-xl border p-5 ${status?.connected ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-start gap-3">
          {status?.connected ? <CheckCircle2 className="text-emerald-600 shrink-0" size={24} /> : <AlertCircle className="text-amber-600 shrink-0" size={24} />}
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-900">
              {status?.connected ? 'FattureInCloud Connesso' : 'FattureInCloud Non Connesso'}
            </h3>
            {status?.connected ? (
              <div className="text-sm text-slate-700 mt-1 space-y-0.5">
                {status.company_name && <div>Azienda: <span className="font-semibold">{status.company_name}</span> {status.company_id && <span className="text-slate-500">(ID {status.company_id})</span>}</div>}
                {status.all_companies?.length > 1 && (
                  <div className="text-xs text-slate-500">Aziende accessibili: {status.all_companies.map(c => c.name).join(', ')}</div>
                )}
                {status.updated_at && <div className="text-xs text-slate-500">Ultima verifica: {new Date(status.updated_at).toLocaleString('it-IT')}</div>}
              </div>
            ) : (
              <p className="text-sm text-slate-700 mt-1">
                Incolla il token manuale generato sul pannello FattureInCloud per abilitare invio/ricezione fatture SDI.
              </p>
            )}
          </div>
          <button onClick={fetchStatus} className="text-slate-500 hover:text-slate-700 p-1.5" title="Aggiorna stato" data-testid="fic-refresh-btn">
            <RefreshCw size={16} />
          </button>
        </div>

        {status?.connected && (
          <div className="mt-4">
            <button onClick={handleDisconnect} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-rose-300 text-rose-700 rounded-md text-sm font-semibold hover:bg-rose-50" data-testid="fic-disconnect-btn">
              <Link2Off size={16} /> Disconnetti
            </button>
          </div>
        )}
      </div>

      {!status?.connected && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <KeyRound size={18} className="text-primary" />
            <h4 className="font-bold text-slate-900">Inserisci il Token Manuale</h4>
          </div>
          <div className="space-y-3">
            <textarea
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="Incolla qui il token che hai generato dal pannello FattureInCloud (di solito inizia con 'a/eyJ...')"
              rows={4}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono focus:ring-2 focus:ring-primary focus:border-primary"
              data-testid="fic-token-input"
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs text-slate-500">
                Lunghezza inserita: <span className="font-mono">{tokenInput.trim().length}</span> caratteri
              </div>
              <button
                onClick={handleSaveToken}
                disabled={saving || !tokenInput.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md text-sm font-bold hover:bg-primary-dark disabled:opacity-60"
                data-testid="fic-save-token-btn"
              >
                {saving ? <><Loader2 size={16} className="animate-spin" /> Verifica in corso...</> : <><Link2 size={16} /> Salva e verifica</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {status?.connected && <AnagraficaSection />}
      {status?.connected && <WebhookSection />}

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h4 className="font-bold text-slate-900 mb-3">Come ottenere il token (Manual Authentication)</h4>
        <ol className="text-sm text-slate-700 space-y-2 list-decimal list-inside">
          <li>Accedi al tuo account <a href="https://secure.fattureincloud.it/settings-apps" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold inline-flex items-center gap-1">FattureInCloud <ExternalLink size={12} /></a></li>
          <li>Vai su <b>Impostazioni → Applicazioni Collegate</b></li>
          <li>Clicca <b>"Collega una nuova applicazione"</b></li>
          <li>Incolla il Client ID dell'app: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">lBdG0Eq2CS1XRh7EiEL6GITHGfkryCHT</code></li>
          <li>Seleziona l'azienda <b>EDIL CIMA S.R.L.</b> dalla lista</li>
          <li>Seleziona i permessi (scope): <code className="text-xs">entity.clients:a</code>, <code className="text-xs">entity.suppliers:a</code>, <code className="text-xs">issued_documents.invoices:a</code>, <code className="text-xs">issued_documents.credit_notes:a</code>, <code className="text-xs">received_documents:a</code>, <code className="text-xs">situation:r</code>, <code className="text-xs">settings:r</code></li>
          <li className="text-xs text-slate-500">I webhook FIC (Generally Available dal 2025) <b>non richiedono scope dedicati</b>: bastano gli scope sopra per ricevere notifiche sulle risorse correlate.</li>
          <li>Clicca <b>Collega/Genera</b> → FIC ti mostra il <b>token completo</b> (formato <code className="text-xs">a/eyJ...</code>)</li>
          <li><b>Copia il token col bottone Copia</b> e incollalo nel campo qui sopra → clicca <b>Salva e verifica</b></li>
        </ol>
        <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500 space-y-1">
          <div><b>Sicurezza:</b> Il token è salvato cifrato in transito sul backend. Non scade mai a meno che tu non lo revochi dal pannello FIC.</div>
          <div><b>Note:</b> Se cambi gli scope dell'app dopo aver salvato il token, NON serve rigenerarlo: i nuovi permessi si applicano automaticamente.</div>
        </div>
      </div>
    </div>
  );
}

function AnagraficaSection() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);

  const loadPreview = async () => {
    try {
      const { data } = await API.get('/api/sdi/anagrafica/preview');
      setPreview(data);
    } catch (e) {
      setPreview({ error: e?.response?.data?.detail || 'Errore' });
    }
  };

  useEffect(() => { loadPreview(); }, []);

  const pull = async (kind) => {
    setBusy(kind);
    try {
      const url = kind === 'clienti' ? '/api/sdi/anagrafica/pull-clienti' : '/api/sdi/anagrafica/pull-fornitori';
      const { data } = await API.post(url);
      alert(`Sync ${kind}: ${data.inserted} nuovi, ${data.updated} aggiornati`);
      await loadPreview();
    } catch (e) {
      alert('Errore: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(null); }
  };

  const syncAll = async () => {
    setBusy('all');
    try {
      const { data } = await API.post('/api/sdi/anagrafica/sync-all');
      alert(`Clienti: ${data.clienti.inserted} nuovi, ${data.clienti.updated} aggiornati\nFornitori: ${data.fornitori.inserted} nuovi, ${data.fornitori.updated} aggiornati`);
      await loadPreview();
    } catch (e) {
      alert('Errore: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(null); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="anagrafica-section">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-bold text-slate-900 flex items-center gap-2"><Users size={18} className="text-primary" /> Anagrafica Unificata FIC ↔ Gestionale</h4>
        <button onClick={syncAll} disabled={busy === 'all'} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-md text-xs font-bold hover:bg-primary-dark disabled:opacity-60" data-testid="sync-all-btn">
          {busy === 'all' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Sincronizza tutto
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Allinea clienti e fornitori tra FattureInCloud e il gestionale (match per P.IVA / CF / Email).
      </p>

      {preview?.error && (
        <p className="text-sm text-red-600 mb-3">⚠ {preview.error}</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AnagraficaCard
          icon={Users}
          label="Clienti"
          data={preview?.clienti}
          onPull={() => pull('clienti')}
          busy={busy === 'clienti'}
          testId="anagrafica-clienti"
        />
        <AnagraficaCard
          icon={Building2}
          label="Fornitori"
          data={preview?.fornitori}
          onPull={() => pull('fornitori')}
          busy={busy === 'fornitori'}
          testId="anagrafica-fornitori"
        />
      </div>
    </div>
  );
}

function AnagraficaCard({ icon: Icon, label, data, onPull, busy, testId }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <p className="font-bold text-slate-800 flex items-center gap-2"><Icon size={16} /> {label}</p>
        <button onClick={onPull} disabled={busy} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 hover:bg-slate-200 rounded text-xs font-semibold disabled:opacity-60" data-testid={`${testId}-pull-btn`}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
          Pull da FIC
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-2xl font-black text-primary">{data?.fic ?? '–'}</p>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Su FIC</p>
        </div>
        <div>
          <p className="text-2xl font-black text-slate-700">{data?.locali ?? '–'}</p>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Locali</p>
        </div>
        <div>
          <p className="text-2xl font-black text-emerald-600">{data?.linkati ?? '–'}</p>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Linkati</p>
        </div>
      </div>
      {(data?.solo_locali || 0) > 0 && (
        <p className="text-xs text-amber-700 mt-3 text-center">
          {data.solo_locali} solo locali: aprire il dettaglio per "Push su FIC"
        </p>
      )}
    </div>
  );
}

function WebhookSection() {
  const [subs, setSubs] = useState(null);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r1 = await API.get('/api/sdi/webhook/list');
      setSubs(r1.data);
    } catch (e) {
      setSubs({ error: e?.response?.data?.detail || 'Errore' });
    }
    try {
      const r2 = await API.get('/api/sdi/webhook/events?limit=5');
      setEvents(r2.data.events || []);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { load(); }, []);

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await API.post('/api/sdi/webhook/subscribe');
      const subId = data.subscription_id;
      const verified = data.verified;
      const warnings = data.warnings || [];
      if (subId) {
        const verifMsg = verified
          ? '\n✅ Sottoscrizione verificata.'
          : '\n⏳ In attesa di verifica: FIC manderà una GET al tuo endpoint per il challenge. Dovrebbe avvenire entro pochi secondi.';
        const warnMsg = warnings.length > 0 ? `\n\n⚠ Avvisi:\n- ${warnings.join('\n- ')}` : '';
        alert(`Webhook registrato! ID: ${subId}\nTipi: ${(data.types_registered || []).length}${verifMsg}${warnMsg}`);
      } else {
        alert(`Webhook NON registrato. Risposta: ${JSON.stringify(data).slice(0, 300)}`);
      }
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      alert('Errore registrazione webhook:\n\n' + detail);
    } finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      await API.post('/api/sdi/sync/run-now');
      alert('Sync avviata in background. Aggiorna le pagine Fatture tra ~30s.\nVengono anche marcate come "scadute" le fatture FIC con data superata.');
    } catch (e) {
      alert('Errore: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const markOverdue = async () => {
    setBusy(true);
    try {
      const { data } = await API.post('/api/sdi/sync/mark-overdue');
      alert(
        `Aggiornamento scadute completato:\n` +
        `• Fatture locali → scadute: ${data.fatture_locali_aggiornate}\n` +
        `• FIC attive non incassate → scadute: ${data.fic_attive_scadute}\n` +
        `• FIC passive non pagate → scadute: ${data.fic_passive_scadute}`
      );
    } catch (e) {
      alert('Errore: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const alignFatture = async () => {
    setBusy(true);
    try {
      const { data } = await API.post('/api/sdi/sync/align-fatture');
      alert(
        `Allineamento Fatture Locali ↔ FIC:\n` +
        `• Allineate (stato SDI aggiornato): ${data.aligned}\n` +
        `• Senza match: ${data.skipped}`
      );
    } catch (e) {
      alert('Errore: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const numSubs = Array.isArray(subs?.data) ? subs.data.length : 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="webhook-section">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-bold text-slate-900 flex items-center gap-2"><Webhook size={18} className="text-primary" /> Webhook & Sync Automatico</h4>
        <button onClick={syncNow} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-md text-xs font-bold disabled:opacity-60" data-testid="sync-now-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Forza sync ora
        </button>
        <button onClick={markOverdue} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-md text-xs font-bold disabled:opacity-60 ml-2" data-testid="mark-overdue-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Aggiorna scadute
        </button>
        <button onClick={alignFatture} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-100 hover:bg-sky-200 text-sky-800 rounded-md text-xs font-bold disabled:opacity-60 ml-2" data-testid="align-fatture-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
          Allinea SDI ↔ Locali
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        <b>Stati che si aggiornano da soli:</b> stato SDI (consegnata/scartata) e stato pagamento via webhook in tempo reale,
        sync di backup ogni 6h, e marcatura "scaduta" automatica all'avvio e ogni 6h per le fatture passive non pagate / attive non incassate oltre la data di scadenza.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold">Sottoscrizioni Attive</p>
            <span className="text-2xl font-black text-primary">{subs?.error ? '–' : numSubs}</span>
          </div>
          <button onClick={subscribe} disabled={busy} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-primary text-white rounded-md text-xs font-bold hover:bg-primary-dark disabled:opacity-60" data-testid="webhook-subscribe-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {numSubs > 0 ? 'Ri-registra webhook' : 'Registra webhook'}
          </button>
          {subs?.error && <p className="text-xs text-amber-700 mt-2">⚠ {subs.error}</p>}
        </div>
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
          <p className="text-sm font-semibold mb-2">Ultimi 5 eventi ricevuti</p>
          {events.length > 0 ? (
            <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
              {events.map((e, i) => (
                <li key={e.received_at || i} className="flex items-center justify-between gap-2">
                  <span className="font-mono truncate text-slate-600">{e.event_type || 'evento'}</span>
                  <span className="text-[10px] text-slate-400 flex-shrink-0">{e.received_at ? new Date(e.received_at).toLocaleString('it-IT') : ''}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-400 italic">Nessun evento ricevuto. Attiva la sottoscrizione qui a sinistra.</p>
          )}
        </div>
      </div>
    </div>
  );
}

